import { ORCHESTRATOR_ENDPOINTS, OPENEMR_ENDPOINTS } from "common/endpoints";
import { request } from "common/services/request";
import { ENVIRONMENT } from "common/constant/environment";

export const getUserConversations = (userId: string, patientId: string) => {
    return request('orchestrator').get(ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.BASE, {
        params: {
            user_id: userId,
            patient_id: patientId,
            limit: 50,
            offset: 0,
        },
    })
}

export const getConversationByShortCode = (shortCode: string, modelRunId?: string) => {
  return request("orchestrator").get(
    ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.BY_SHORT_CODE(shortCode),
    {
      params: modelRunId ? { model_run_id: modelRunId } : undefined,
    }
  );
};

type CreateConversationPayload = {
  title: string;
  user_id: string;
  patient_id: string;
  model_run_id: string;
  short_code: string;
};

export const createUserConversation = (payload: CreateConversationPayload) => {
  return request("orchestrator").post(
    ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.BASE,
    payload
  );
};

export const updateConversationStatus = (conversationId: string, status: string) => {
  return request("orchestrator").patch(
    ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.BY_ID(conversationId),
    { status }
  );
};

export const deleteConversation = (conversationId: string) => {
  return request("orchestrator").delete(
    ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.BY_ID(conversationId)
  );
};

export const updateConversationSummary = (conversationId: string, summary: string) => {
  return request("orchestrator").put(
    ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.SUMMARY.BY_ID(conversationId),
    { prior_summary: summary }
  );
};

export const getConversationSummary = (conversationId: string) => {
  return request("orchestrator").get(
    ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.SUMMARY.BY_ID(conversationId)
  );
};

export const deleteConversationSummary = (conversationId: string) => {
  return request("orchestrator").delete(
    ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.SUMMARY.BY_ID(conversationId)
  );
};

export const getCopilotResponsesByRunId = (runId: string, targetLang?: string) => {
  return request("orchestrator").get(
    ORCHESTRATOR_ENDPOINTS.MODEL_RUNS.COPILOT_RESPONSE(runId),
    {
      params: targetLang && targetLang !== 'en' ? { target_lang: targetLang } : undefined,
    }
  );
};

export const getPatientMetadata = (subjectId: string) => {
  if (ENVIRONMENT === "dev") {
    // OpenEMR credentials live server-side; the orchestrator ignores any
    // client_id/client_secret in the body, so none are sent.
    return request("orchestrator").post(OPENEMR_ENDPOINTS.OPENEMR_PATIENTS, {}).then((res) => {
      const items = Array.isArray(res.data) ? res.data : [];
      // try matching by uuid first, then subject_id
      const found = items.find((p: any) => String(p.uuid) === String(subjectId) || String(p.subject_id) === String(subjectId));
      return { data: found || { subject_id: subjectId } };
    });
  }

  return request("orchestrator").get(
    ORCHESTRATOR_ENDPOINTS.PATIENT.BY_SUBJECT_ID(subjectId)
  );
};

export const getModelRunsByPatientId = (
  patientId: string,
  limit: number = 50,
  offset: number = 0
) => {
  return request("orchestrator")
    .get(ORCHESTRATOR_ENDPOINTS.MODEL_RUNS.BASE, {
      params: {
        patient_id: patientId,
        limit,
        offset,
      },
    })
    .then((res) => ({
      data: res.data as any[],
      totalCount: parseInt(res.headers["x-total-count"] ?? "0", 10),
    }));
};

export const getPatientTimeSeries = (runId: string) => {
  return request("orchestrator").get(
    ORCHESTRATOR_ENDPOINTS.MODEL_RUNS.TIME_SERIES(runId)
  );
};

export const getPatientForecasts = (runId: string) => {
  return request("orchestrator").get(
    ORCHESTRATOR_ENDPOINTS.MODEL_RUNS.FORECASTS(runId)
  );
};

export const getPatientThresholds = (patientId: string) => {
  return request("orchestrator").get(
    ORCHESTRATOR_ENDPOINTS.MODEL_RUNS.PATIENT_THRESHOLDS(patientId)
  );
};

// --- Patient memory facts (the Facts panel) -------------------------------

export type FactStatusFilter = "all" | "active" | "invalid";

export const getPatientFacts = (
  patientId: string,
  status: FactStatusFilter = "all"
) => {
  return request("orchestrator").get(
    ORCHESTRATOR_ENDPOINTS.MEMORY.FACTS(patientId),
    { params: { status } }
  );
};

export const updatePatientFact = (
  patientId: string,
  factUuid: string,
  fact: string
) => {
  return request("orchestrator").patch(
    ORCHESTRATOR_ENDPOINTS.MEMORY.FACT_BY_ID(patientId, factUuid),
    { fact }
  );
};

export const invalidatePatientFact = (patientId: string, factUuid: string) => {
  return request("orchestrator").post(
    ORCHESTRATOR_ENDPOINTS.MEMORY.FACT_INVALIDATE(patientId, factUuid)
  );
};

export const restorePatientFact = (patientId: string, factUuid: string) => {
  return request("orchestrator").post(
    ORCHESTRATOR_ENDPOINTS.MEMORY.FACT_RESTORE(patientId, factUuid)
  );
};

export const deletePatientFact = (patientId: string, factUuid: string) => {
  return request("orchestrator").delete(
    ORCHESTRATOR_ENDPOINTS.MEMORY.FACT_BY_ID(patientId, factUuid)
  );
};

export const addPatientFact = (patientId: string, text: string) => {
  return request("orchestrator").post(
    ORCHESTRATOR_ENDPOINTS.MEMORY.FACTS(patientId),
    { text }
  );
};

// Server-truth extraction status: `episodes_pending` > 0 while this patient's
// episodes are queued/extracting. The panel polls this so the "adding…"
// indicator survives a page refresh (pending state is no longer client-only).
export const getPatientMemoryStatus = (patientId: string) => {
  return request("orchestrator").get(
    ORCHESTRATOR_ENDPOINTS.MEMORY.STATUS(patientId)
  );
};
